import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Quote, QuoteType, LeadCommunicationEntry, LeadCommunicationType } from '../types/admin';

export type LeadSortField = 'createdAt' | 'customerCompanyName' | 'movingDate' | 'nextFollowUp';
export type SortDir = 'asc' | 'desc';

export interface LeadStats {
  new: number;
  contacted: number;
  estimate_scheduled: number;
  total: number;
}

interface ListResponse {
  success: boolean;
  data: Quote[];
  hasMore: boolean;
  nextCursor?: string;
  stats: Record<string, number>;
  leadStats: LeadStats;
}

export interface UseLeadsParams {
  search?: string;
  sortBy?: LeadSortField;
  sortDir?: SortDir;
  leadUid?: string;
  type?: QuoteType;
}

const PAGE_SIZE = 20;

function buildLeadQuery(params: UseLeadsParams, cursor?: string): string {
  const qs = new URLSearchParams();
  qs.set('limit', String(PAGE_SIZE));
  qs.set('lifecycleStage', 'lead');
  if (cursor) qs.set('cursor', cursor);
  if (params.search) qs.set('search', params.search);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  if (params.leadUid) qs.set('leadUid', params.leadUid);
  if (params.type) qs.set('type', params.type);
  return qs.toString();
}

/**
 * Fetches and manages a paginated list of leads (quotes with lifecycleStage === 'lead').
 * Supports search, sort, filtering by lead, and infinite scroll.
 * Uses React Query for caching and deduplication.
 */
export function useLeads(orgId: string | null, params: UseLeadsParams = {}) {
  const queryClient = useQueryClient();
  const { search, sortBy = 'createdAt', sortDir = 'desc', leadUid, type } = params;
  const resolvedParams = { search, sortBy, sortDir, leadUid, type };

  const queryKey = ['leads', orgId, resolvedParams] as const;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error: queryError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const qs = buildLeadQuery(resolvedParams, pageParam as string | undefined);
      return api.get<ListResponse>(`/quotes/${orgId}?${qs}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: !!orgId,
  });

  const leads = data?.pages.flatMap(p => p.data) ?? [];
  const stats = data?.pages[0]?.leadStats ?? { new: 0, contacted: 0, estimate_scheduled: 0, total: 0 };

  /**
   * Create a new move record. Accepts a payload that already has
   * `lifecycleStage` set by the caller (typically NewMoveForm).
   * This replaces the old `createLead` that hardcoded `lifecycleStage: 'lead'`.
   */
  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (!orgId) throw new Error('No active org');
      const res = await api.post<{ success: boolean; data: Quote }>(`/quotes/${orgId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      queryClient.invalidateQueries({ queryKey: ['quotes', orgId] });
    },
  });

  const createMove = useCallback(
    async (payload: Record<string, unknown>): Promise<Quote> => {
      return createMutation.mutateAsync(payload);
    },
    [createMutation],
  );

  const logCommunication = useCallback(
    async (
      leadId: string,
      entry: { type: LeadCommunicationType; note: string; createdBy: string; createdByName: string; nextFollowUp?: string },
    ): Promise<Quote> => {
      if (!orgId) throw new Error('No active org');
      const lead = leads.find(l => l.id === leadId);
      const existingLog = lead?.leadCommunicationLog ?? [];
      const newEntry: LeadCommunicationEntry = {
        id: crypto.randomUUID(),
        type: entry.type,
        note: entry.note,
        createdAt: new Date().toISOString(),
        createdBy: entry.createdBy,
        createdByName: entry.createdByName,
      };
      const autoProgress = lead?.leadStatus === 'new' || !lead?.leadStatus;
      const patch: Record<string, unknown> = {
        leadCommunicationLog: [...existingLog, newEntry],
        ...(autoProgress ? { leadStatus: 'contacted' } : {}),
        ...(entry.nextFollowUp !== undefined ? { nextFollowUp: entry.nextFollowUp || null } : {}),
      };
      const res = await api.patch<{ success: boolean; data: Quote }>(
        `/quotes/${orgId}/${leadId}`,
        patch,
      );
      // Invalidate to refresh the list with updated data.
      queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      return res.data;
    },
    [orgId, leads, queryClient],
  );

  const fetchMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    leads,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load leads') : null,
    hasMore: !!hasNextPage,
    stats,
    createMove,
    logCommunication,
    fetchMore,
    refetch,
  };
}
